// SessionManager.tsx
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import {
  restoreSession,
  hydrateFromStorage,
  selectIsAuthenticated,
  selectAuthLoading,
  selectSessionChecked,
  selectUser,
} from 'src/reducers/authSlice';
import { fetchOrgSettings } from 'src/reducers/settingsSlice';
import { fetchAllCodeTables } from 'src/reducers/codeTableSlice';
import { fetchPublicTenantConfig, fetchAuthenticatedTenantConfig } from 'src/reducers/tenantConfigSlice';

const PUBLIC_ROUTES = ['/login', '/login-old', '/forget-password', '/resetpassword', '/portal/login', '/onboarding/login', '/nlaunch/signup'];

const SessionManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading = useAppSelector(selectAuthLoading);
  const sessionChecked = useAppSelector(selectSessionChecked);
  const user = useAppSelector(selectUser);


  useEffect(() => {
    // First, try to hydrate from sessionStorage synchronously
    dispatch(hydrateFromStorage());
    // Note: Public tenant config is now fetched by TenantRouteRedirect or the login page itself
  }, [dispatch]);

  useEffect(() => {
    // After hydration, if not authenticated, try to restore session from backend
    if (!isAuthenticated && !sessionChecked) {
      dispatch(restoreSession());
    }
  }, [dispatch, isAuthenticated, sessionChecked]);

  // Fetch org settings when authenticated (skip for client users - they don't need org settings)
  //
  // Depend on user?.userId (a stable primitive), NOT the user object itself.
  // A fresh client-side login triggers hydrateFromStorage() AND races against
  // this component's own restoreSession() (dispatched on mount, checking for
  // an existing session). When restoreSession() resolves after hydration
  // already ran, it replaces `user` with a new object reference for the SAME
  // logical user, which — if this depended on the object — would re-trigger
  // this effect and re-dispatch fetchOrgSettings/fetchAuthenticatedTenantConfig
  // a second time. Depending on userId only re-fires when the actual logged-in
  // user changes.
  useEffect(() => {
    if (isAuthenticated && sessionChecked && user) {
      const roleName = (user.roles?.[0]?.name || user.role?.name || '').trim();
      const isClient = roleName === 'Client' || roleName === 'Client Admin';
      if (!isClient) {
        dispatch(fetchOrgSettings());
        dispatch(fetchAuthenticatedTenantConfig());
      }
    }
  }, [dispatch, isAuthenticated, sessionChecked, user?.userId]);

  // Fetch code tables when authenticated (cached globally, only fetches once)
  // Clients need code tables for document categories, compliance types, etc.
  useEffect(() => {
    if (isAuthenticated && sessionChecked) {
      dispatch(fetchAllCodeTables());
    }
  }, [dispatch, isAuthenticated, sessionChecked]);

  useEffect(() => {
    // After session check is complete, redirect if needed
    if (sessionChecked) {
      // Strip /t/:tenantRef prefix before checking public routes
      // TenantAutoRedirect may redirect to /t/rds-test/resetpassword/TOKEN
      // which must still be treated as a public route
      const pathForCheck = location.pathname.replace(/^\/t\/[^/]+/, '');
      const isPublicRoute = PUBLIC_ROUTES.some(route =>
        pathForCheck.startsWith(route)
      );

      // Helper to check if user is a client
      const isClientUser = () => {
        if (!user) return false;
        const roleName = (user.roles?.[0]?.name || user.role?.name || '').trim();
        return roleName === 'Client' || roleName === 'Client Admin';
      };

      // Match the external portal routes EXACTLY (/portal, /portal/...; /onboarding, /onboarding/...; /vendor, /vendor/...).
      // Without the trailing-slash check, paths like /onboarding-admin/invite-new-hire would
      // be misclassified as portal routes and the admin would get redirected away.
      const isOnPortalRoute = location.pathname === '/portal' || location.pathname.startsWith('/portal/');
      const isOnOnboardingRoute = location.pathname === '/onboarding' || location.pathname.startsWith('/onboarding/');
      const isOnVendorRoute = location.pathname === '/vendor' || location.pathname.startsWith('/vendor/');

      // Helper to check if user has Onboarding role
      const isOnboardingUser = () => {
        if (!user) return false;
        const roleName = (user.roles?.[0]?.name || user.role?.name || '').trim();
        return roleName === 'Onboarding';
      };

      // Helper to check if user has Vendor role (for the new vendor portal)
      const isVendorUser = () => {
        if (!user) return false;
        const roleName = (user.roles?.[0]?.name || user.role?.name || '').trim();
        return roleName === 'Vendor';
      };

      if (isAuthenticated) {
        // Client Portal Guard Logic
        const clientUser = isClientUser();
        const onboardingUser = isOnboardingUser();
        const vendorUser = isVendorUser();

        // Client users should only access portal routes
        if (clientUser && !isOnPortalRoute) {
          navigate('/portal', { replace: true });
          return;
        }

        // Onboarding users should only access onboarding routes
        if (onboardingUser && !isOnOnboardingRoute) {
          navigate('/onboarding', { replace: true });
          return;
        }

        // Vendor users should only access vendor portal routes
        if (vendorUser && !isOnVendorRoute) {
          navigate('/vendor', { replace: true });
          return;
        }

        // Non-client users should not access portal routes (except login)
        if (!clientUser && isOnPortalRoute && location.pathname !== '/portal/login') {
          navigate('/', { replace: true });
          return;
        }

        // Non-onboarding users should not access onboarding routes (except login)
        if (!onboardingUser && isOnOnboardingRoute && location.pathname !== '/onboarding/login') {
          navigate('/', { replace: true });
          return;
        }

        // Non-vendor users should not access vendor portal routes (except login)
        if (!vendorUser && isOnVendorRoute && location.pathname !== '/vendor/login') {
          navigate('/', { replace: true });
          return;
        }

        // NOTE: We no longer do "reverse redirect" for authenticated users on public routes.
        // The login page handles its own navigation to /post-login on success.
        // This prevents race conditions where SessionManager overrides the login page's navigation.
      } else {
        // Normal guard: If not on a public route and not logged in, go to login
        if (!isPublicRoute) {
          // Check if trying to access portal/onboarding/vendor route - redirect to respective login.
          // Same trailing-slash discipline as above so admin pages like /onboarding-admin/...
          // don't get matched and bounced to /onboarding/login.
          const isOnPortalRoute = location.pathname === '/portal' || location.pathname.startsWith('/portal/');
          const isOnOnboardingRoute = location.pathname === '/onboarding' || location.pathname.startsWith('/onboarding/');
          const isOnVendorRoute = location.pathname === '/vendor' || location.pathname.startsWith('/vendor/');
          const loginTarget = isOnPortalRoute
            ? '/portal/login'
            : isOnOnboardingRoute
            ? '/onboarding/login'
            : isOnVendorRoute
            ? '/vendor/login'
            : '/login';
          navigate(loginTarget, {
            state: { from: location.pathname },
            replace: true,
          });
        }
      }
    }
  }, [sessionChecked, isAuthenticated, user, location.pathname, navigate]);

  // Show loader while checking session
  if (loading || (!sessionChecked && !isAuthenticated)) {
    return (
      <div className="centered-loader-container">
        <div className="centered-loader">
          <SuspenseLoader />
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default SessionManager;